import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  TrainingGoal,
  TrainingGoalWithProgress,
  CreateTrainingGoalData,
  UpdateTrainingGoalData
} from '@/types/trainingGoals';
import { BeltColor, BeltColorConfig } from '@/constants/beltColors';
import { useI18n } from '@/contexts/I18nContext';
import { checkTrainingGoalProgress } from '@/utils/trainingGoalNotifications';

// Belt color configuration mapping
const BELT_COLORS: Record<BeltColor, BeltColorConfig> = {
  white: {
    base: 'bg-white',
    filled: 'bg-gray-100',
    opacity: 'bg-white/30'
  },
  blue: {
    base: 'bg-blue-500',
    filled: 'bg-blue-600',
    opacity: 'bg-blue-500/30'
  },
  purple: {
    base: 'bg-purple-500',
    filled: 'bg-purple-600',
    opacity: 'bg-purple-500/30'
  },
  brown: {
    base: 'bg-amber-700',
    filled: 'bg-amber-800',
    opacity: 'bg-amber-700/30'
  },
  black: {
    base: 'bg-gray-900',
    filled: 'bg-black',
    opacity: 'bg-gray-900/30'
  }
};

export const useTrainingGoals = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const { data: goal, isLoading, error, refetch } = useQuery({
    queryKey: ['training-goal', user?.id],
    queryFn: async (): Promise<TrainingGoalWithProgress | null> => {
      if (!user?.id) return null;

      // Get the active training goal
      const { data: goalData, error: goalError } = await supabase
        .from('training_goals')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle(); // Use maybeSingle instead of single to avoid 406 error

      if (goalError) {
        // Log error but don't throw for missing goals
        console.error('Error fetching training goal:', goalError);
        return null;
      }

      // If no goal found, return null
      if (!goalData) {
        return null;
      }

      // Get progress for this goal
      const { data: progressData, error: progressError } = await supabase
        .from('training_goal_progress')
        .select('*')
        .eq('goal_id', goalData.id)
        .order('week_number', { ascending: true });

      if (progressError) throw progressError;

      // Get completed trainings for the user
      const { data: trainingsData, error: trainingsError } = await supabase
        .from('trainings')
        .select('id, end_time, status')
        .eq('user_id', user.id)
        .eq('status', 'Completed');

      if (trainingsError) throw trainingsError;

      // Calculate weekly progress based on goal start date (30-day cycle)
      const goalStartDate = new Date(goalData.start_date);
      const currentDate = new Date();
      
      // Check if we're still within the 30-day cycle
      const daysSinceStart = Math.floor((currentDate.getTime() - goalStartDate.getTime()) / (1000 * 60 * 60 * 24));
      const isWithinCycle = daysSinceStart < 30;
      
      // Calculate weekly progress for each of the 4 weeks
      const weeklyProgress = [];
      for (let week = 1; week <= 4; week++) {
        const weekStart = new Date(goalStartDate);
        weekStart.setDate(weekStart.getDate() + (week - 1) * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        
        // Only count progress if the week has passed or is current
        const weekHasPassed = currentDate >= weekStart;
        const isCurrentWeek = currentDate >= weekStart && currentDate <= weekEnd;
        
        if (weekHasPassed) {
          // Count trainings for this specific week
          const weekTrainings = trainingsData?.filter(training => {
            const trainingDate = new Date(training.end_time);
            return trainingDate >= weekStart && trainingDate <= weekEnd;
          }) || [];
          
          const weekCompleted = weekTrainings.length;
          const weekGoalMet = weekCompleted >= goalData.weekly_goal;
          
          weeklyProgress.push({
            week: week,
            completed: weekCompleted,
            goal: goalData.weekly_goal,
            met: weekGoalMet,
            startDate: weekStart,
            endDate: weekEnd
          });
        } else {
          // Future week
          weeklyProgress.push({
            week: week,
            completed: 0,
            goal: goalData.weekly_goal,
            met: false,
            startDate: weekStart,
            endDate: weekEnd
          });
        }
      }

      // Get completed trainings for CURRENT month (not goal start date)
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      // Primeiro, vamos ver quantos treinos o usuário tem no total
      const { data: allTrainings, error: allTrainingsError } = await supabase
        .from('trainings')
        .select('id, end_time, status')
        .eq('user_id', user.id);

      if (allTrainingsError) throw allTrainingsError;

      // Filtrar por mês no JavaScript para debug
      const currentMonthTrainings = trainingsData?.filter(training => {
        const trainingDate = new Date(training.end_time);
        const isInCurrentMonth = trainingDate >= currentMonthStart && trainingDate <= currentMonthEnd;
        
        return isInCurrentMonth;
      }) || [];

      const totalCompleted = currentMonthTrainings.length;

      // Calculate current week progress (based on current month, not goal start date)
      const currentWeekStart = new Date(now);
      currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay()); // Start of current week (Sunday)
      const currentWeekEnd = new Date(currentWeekStart);
      currentWeekEnd.setDate(currentWeekEnd.getDate() + 6); // End of current week (Saturday)

      const currentWeekTrainings = trainingsData?.filter(training => {
        const trainingDate = new Date(training.end_time);
        return trainingDate >= currentWeekStart && trainingDate <= currentWeekEnd;
      }) || [];

      const currentWeekProgress = currentWeekTrainings.length;

      // Check if monthly goal is met
      const isMonthlyGoalMet = totalCompleted >= goalData.monthly_goal;

      // Calculate current week number (1-4)
      const currentWeek = Math.min(Math.floor(currentWeekProgress / goalData.weekly_goal) + 1, 4);

      return {
        ...goalData,
        progress: progressData || [],
        current_week_progress: currentWeekProgress,
        total_completed: totalCompleted,
        is_monthly_goal_met: isMonthlyGoalMet,
        current_week_number: currentWeek,
        weekly_progress: weeklyProgress,
        is_within_cycle: isWithinCycle,
        days_since_start: daysSinceStart
      };
    },
    enabled: !!user?.id,
    staleTime: 0, // Sempre considerar dados como stale para forçar atualização
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const createGoalMutation = useMutation({
    mutationFn: async (goalData: CreateTrainingGoalData): Promise<TrainingGoal> => {
      if (!user?.id) throw new Error('User not authenticated');

      const weeklyGoal = Math.ceil(goalData.monthly_goal / 4);
      const startDate = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('training_goals')
        .insert({
          user_id: user.id,
          monthly_goal: goalData.monthly_goal,
          weekly_goal: weeklyGoal,
          start_date: startDate
        })
        .select()
        .single();

      if (error) throw error;

      // Create initial progress records for all 4 weeks
      const progressRecords = [];
      for (let week = 1; week <= 4; week++) {
        const weekStart = new Date(startDate);
        weekStart.setDate(weekStart.getDate() + (week - 1) * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        progressRecords.push({
          goal_id: data.id,
          week_number: week,
          week_start_date: weekStart.toISOString().split('T')[0],
          week_end_date: weekEnd.toISOString().split('T')[0]
        });
      }

      const { error: progressError } = await supabase
        .from('training_goal_progress')
        .insert(progressRecords);

      if (progressError) throw progressError;

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-goal'] });
    },
  });

  const updateGoalMutation = useMutation({
    mutationFn: async (goalData: UpdateTrainingGoalData): Promise<TrainingGoal> => {
      if (!user?.id || !goal) throw new Error('User not authenticated or no goal found');

      const weeklyGoal = Math.ceil(goalData.monthly_goal / 4);

      const { data, error } = await supabase
        .from('training_goals')
        .update({
          monthly_goal: goalData.monthly_goal,
          weekly_goal: weeklyGoal,
          updated_at: new Date().toISOString()
        })
        .eq('id', goal.id)
        .select()
        .single();

      if (error) throw error;

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-goal'] });
    },
  });

  const deleteGoalMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!user?.id || !goal) throw new Error('User not authenticated or no goal found');

      const { error } = await supabase
        .from('training_goals')
        .delete()
        .eq('id', goal.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-goal'] });
    },
  });

  // Function to check goal progress and create notifications
  const checkGoalProgress = async () => {
    if (user?.id) {
      await checkTrainingGoalProgress(user.id, t);
      // Refresh the goal data after checking progress
      await refetch();
    }
  };

  return {
    goal,
    isLoading,
    error,
    refetch,
    createGoal: createGoalMutation.mutateAsync,
    updateGoal: updateGoalMutation.mutateAsync,
    deleteGoal: deleteGoalMutation.mutateAsync,
    isCreating: createGoalMutation.isPending,
    isUpdating: updateGoalMutation.isPending,
    isDeleting: deleteGoalMutation.isPending,
    beltColors: BELT_COLORS,
    checkGoalProgress
  };
};
